import React from 'react'
import _ from 'underscore'
import { tz } from 'moment-timezone'
import styled from 'styled-components'

import { Panel } from 'components/Panel'
import Spinner from 'components/Spinner'

import RotateDeviceMessage from 'pages/shared/RotateDeviceMessage'
import MaintenanceTrend from 'pages/shared/MaintenanceTrend'
import { parseAlerts } from 'pages/Device/ThermostatRthPanel/Chart/rthDataParser'

import BrowserCompatibility from 'helpers/browserCompatibility'

import useAlertsHistory from 'hooks/useAlertsHistory'
import useOrientation from 'hooks/useOrientation'
import useDeviceMaintenanceTrends from 'hooks/useDeviceMaintenanceTrends'

import { updateAlertsSeverity } from 'helpers/alerts'

import { Device } from 'types/Device'
import { DeviceMaintenanceTrend } from 'types/DeviceMetric'

type props = {
  device: Device
  dealerId: number
}

const MaintenanceTrendsPanel = ({ dealerId, device }: props) => {
  const { maintenanceTrends, areMaintenanceTrendsLoaded } = useDeviceMaintenanceTrends(device.deviceId)
  const isMobile = BrowserCompatibility.isMobile(navigator.userAgent)
  const { isPortrait } = useOrientation()
  const alertHistoryToTime = tz(device.timeZone).endOf('day')
  const alertHistoryFromTime = alertHistoryToTime.clone().subtract(30, 'days').startOf('day')
  const deviceId = device.deviceId
  const { alertsHistory, areAlertsHistoryLoaded } = useAlertsHistory({ dealerId, deviceId, fromTime: alertHistoryFromTime.format(), toTime: alertHistoryToTime.format(), deviceTimeZone: device.timeZone })

  if (areMaintenanceTrendsLoaded && _.isEmpty(maintenanceTrends)) {
    return (<></>)
  }

  return (
    <S.Panel id="maintenance-trends" scrollToSection>
      <Panel.Header>
        <Panel.Title>Maintenance Trends</Panel.Title>
      </Panel.Header>
      {isMobile && isPortrait
        ? (
          <RotateDeviceMessage />
        )
        : (
          <div>
            {(!areMaintenanceTrendsLoaded || !areAlertsHistoryLoaded) && <Spinner variant="page" />}
            {areMaintenanceTrendsLoaded && maintenanceTrends && (
              _.map(maintenanceTrends, (maintenanceTrend: DeviceMaintenanceTrend) => {
                const sortedMetricsAndGroups = _.sortBy([...maintenanceTrend.metrics, ...maintenanceTrend.groups], ['sortPosition'])
                const alertCodes = _.sortBy([...maintenanceTrend.alertList], ['sortPosition'])
                const historyDetails = updateAlertsSeverity(alertsHistory?.historyDetails) ?? []
                const selectedCodes = _.pluck(alertCodes, 'code')
                const alerts = parseAlerts(historyDetails.filter((alert) => selectedCodes.includes(alert.code)))
                return (
                  <MaintenanceTrend
                    key={maintenanceTrend.name}
                    trend={maintenanceTrend}
                    metricAndGroupList={sortedMetricsAndGroups}
                    alerts={alerts}
                    timeZone={device.timeZone}
                    deviceId={device.deviceId}
                    showMetricSelection
                    sendAnalyticsEvents
                  />
                )
              })
            )}
          </div>
        )}
    </S.Panel>
  )
}

export default MaintenanceTrendsPanel

const S = {
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
  `
}
